import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Tooltip } from "../../../ui/tooltip";
import { OverflowTabs, TabItem } from "../../../ui/overflow-tabs";
import { 
  Settings,
  ChevronLeft,
  Wrench,
  Sliders,
  Tag,
  MessageSquare,
  Database,
} from "lucide-react";
import { 
  ConfigSidebarState, 
  InterjectionGroup, 
  InterjectionType,
  FlowData,
  DecisionVariablesRegistry
} from "../utils/types";
import ConfigTab from "./ConfigTab";
import InterjectionsTab from "./InterjectionsTab";
import ToolCallsTab from "./ToolCallsTab";
import PlaceholdersTab from "./PlaceholdersTab";
import VariablesTab from "./VariablesTab";



interface ConfigSidebarProps {
  configState: ConfigSidebarState;
  
  // New group-based handlers
  onStartAddingNewGroup: () => void;
  onStartEditingGroup: (group: InterjectionGroup) => void;
  onCancelEditing: () => void;
  onSaveInterjectionGroup: (groupData: {
    type: InterjectionType;
    customTypeName?: string;
    groupName?: string;
    interjection_rate: number;
    within_script: boolean;
    repeat_script: boolean;
  }) => void;
  onDeleteInterjectionGroup: (group: InterjectionGroup) => void;
  onStartAddingContent: (groupId: string) => void;
  onStartEditingContent: (groupId: string, contentId: string) => void;
  onSaveContent: (interjection_text: string, interjection_response: string) => void;
  onSaveContentForGroup?: (groupId: string, interjection_text: string, interjection_response: string) => void;
  onDeleteContent: (groupId: string, contentId: string) => void;
  
  // Export/Import/Load handlers
  onExportFlow: () => void;
  onImportFlow: (flowData: FlowData) => void;
  onLoad: () => void;
  onLoadTree: (tree: any) => void;
  onClearDecisionVariables: () => void;
  onTriggerFinetune: () => void;
  
  // Tree status management
  currentTreeStatus: 'UNSAVED' | 'SAVED' | 'READY_FOR_FINE_TUNE' | 'RUNNING' | 'FAILED' | 'COMPLETED';
  currentTreeName: string;
  onTreeStatusChange?: (status: 'UNSAVED' | 'SAVED' | 'READY_FOR_FINE_TUNE' | 'RUNNING' | 'FAILED' | 'COMPLETED') => void;
  
  // Placeholder management handlers
  onUpdateGlobalVariableRegistry: (registry: any) => void;
  onEditNode: (nodeId: string) => void;
  onNodeFocus?: (nodeId: string) => void;
  onEditToolCall?: (originalSignature: string, toolCall: any) => void;
  
  // Boolean variables management handlers
  onUpdateDecisionVariablesRegistry: (registry: DecisionVariablesRegistry) => void;
  onUpdateNodes?: (nodes: any[]) => void;
  
  // System prompt handlers
  systemPrompt: string;
  onSystemPromptChange: (prompt: string) => void;
  
  // Flow data for export/import
  nodes: any[];
  edges: any[];
  globalVariableRegistry: any;
  decisionVariablesRegistry: DecisionVariablesRegistry;
  identifiers: string[];
  interjectionGroups: InterjectionGroup[];
  
  // Loaded tree metadata
  loadedTreeMetadata?: {
    id: number;
    conversation_flow_name: string;
    application_name: string;
    intent_name: string;
    conversation_flow_version: number;
    is_latest: boolean;
  } | null;
}

const ConfigSidebar: React.FC<ConfigSidebarProps> = ({
  configState,
  onStartAddingNewGroup,
  onStartEditingGroup,
  onCancelEditing,
  onSaveInterjectionGroup,
  onDeleteInterjectionGroup,
  onStartAddingContent,
  onStartEditingContent,
  onSaveContent,
  onSaveContentForGroup,
  onDeleteContent,
  onExportFlow,
  onImportFlow,
  onLoad,
  onLoadTree,
  onClearDecisionVariables,
  onTriggerFinetune,
  currentTreeStatus,
  currentTreeName,
  onTreeStatusChange,
  onUpdateGlobalVariableRegistry,
  onUpdateDecisionVariablesRegistry,
  onUpdateNodes,
  onNodeFocus,
  onEditToolCall,
  systemPrompt,
  onSystemPromptChange,
  nodes,
  globalVariableRegistry,
  decisionVariablesRegistry,
  loadedTreeMetadata,
}: ConfigSidebarProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("config");

  // Define tabs for OverflowTabs component
  const tabs: TabItem[] = [
    {
      key: "config",
      label: "Config",
      icon: <Settings className="w-3 h-3" />
    },
    {
      key: "interjections",
      label: "Interjections",
      icon: <MessageSquare className="w-3 h-3" />
    },
    {
      key: "tools",
      label: "Tools",
      icon: <Wrench className="w-3 h-3" />
    },
    {
      key: "placeholders",
      label: "Placeholders",
      icon: <Tag className="w-3 h-3" />
    },
    {
      key: "variables",
      label: "Variables",
      icon: <Database className="w-3 h-3" />
    }
  ];





  // Handle the expand/collapse toggle
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };



  // Render collapsed toggle button
  if (!isExpanded) {
    return (
      <div className="fixed top-1/2 right-0 -translate-y-1/2 z-60">
        <Tooltip content="Configure your conversation tree • Config • Interjections • Tools • Placeholders • Variables" side="left" immediate={true}>
          <Button
            onClick={handleToggleExpanded}
            className="bg-maitai-green hover:bg-maitai-green/90 text-white rounded-l-lg rounded-r-none px-4 py-3 min-h-20"
          >
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-2">
                <Sliders className="w-6 h-6" />
                <span className="font-medium">CONFIG</span>
              </div>
              <ChevronLeft className="w-6 h-6 rotate-180" />
            </div>
          </Button>
        </Tooltip>
      </div>
    );
  }



  return (
    <Sheet open={isExpanded} onOpenChange={handleToggleExpanded}>
      <SheetContent 
        side="right" 
        className="!w-[500px] !max-w-[600px] p-0 bg-accent/90 flex flex-col h-full"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="space-y-1">
              <SheetTitle className="text-xl font-gc-med text-foreground">
                Configuration
              </SheetTitle>
              <p className="text-sm text-muted-foreground font-gc-reg">
                Conversation Tree settings and tools
              </p>
            </div>
          </div>
        </SheetHeader>
        <div className="flex-1 flex flex-col h-full -mt-4">
          <OverflowTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="border-b border-border"
            tabClassName="text-xs font-gc-med px-2.5 py-2"
            activeTabClassName="bg-maitai-green/10 text-maitai-green"
            inactiveTabClassName="text-muted-foreground hover:text-foreground hover:bg-accent/50"
          />
          
          {activeTab === "interjections" && (
            <div className="px-6 py-3 bg-accent/30 border-b border-border">
              <p className="text-xs text-muted-foreground font-gc-reg">
                Add dynamic interruptions, FAQs, and contextual responses that can be triggered during conversations at configurable rates.
              </p>
            </div>
          )}
          
          <div className="flex-1">
            {activeTab === "config" && (
              <ConfigTab 
                onExportFlow={onExportFlow}
                onImportFlow={onImportFlow}
                onLoad={onLoad}
                onLoadTree={onLoadTree}
                onClearDecisionVariables={onClearDecisionVariables}
                onTriggerFinetune={onTriggerFinetune}
                currentTreeStatus={currentTreeStatus}
                currentTreeName={currentTreeName}
                onTreeStatusChange={onTreeStatusChange}
                systemPrompt={systemPrompt}
                onSystemPromptChange={onSystemPromptChange}
                loadedTreeMetadata={loadedTreeMetadata}
              />
            )}
          
            {activeTab === "interjections" && (
          <InterjectionsTab 
                configState={configState}
                onStartAddingNewGroup={onStartAddingNewGroup}
                onStartEditingGroup={onStartEditingGroup}
                onCancelEditing={onCancelEditing}
                onSaveInterjectionGroup={onSaveInterjectionGroup}
                onDeleteInterjectionGroup={onDeleteInterjectionGroup}
                onStartAddingContent={onStartAddingContent}
                onStartEditingContent={onStartEditingContent}
                onSaveContent={onSaveContent}
                onDeleteContent={onDeleteContent}
                onSaveContentForGroup={onSaveContentForGroup}
              />
            )}
          
            {activeTab === "tools" && (
              <ToolCallsTab 
                nodes={nodes}
                onNodeFocus={onNodeFocus}
                onEditToolCall={onEditToolCall}
              />
            )}
          
            {activeTab === "placeholders" && (
              <PlaceholdersTab 
                nodes={nodes}
                globalVariableRegistry={globalVariableRegistry}
                onUpdateGlobalVariableRegistry={onUpdateGlobalVariableRegistry}
                onNodeFocus={onNodeFocus}
              />
            )}
          
            {activeTab === "variables" && (
              <VariablesTab 
                decisionVariablesRegistry={decisionVariablesRegistry}
                onUpdateDecisionVariablesRegistry={onUpdateDecisionVariablesRegistry}
                nodes={nodes}
                onUpdateNodes={onUpdateNodes}
                onNodeFocus={onNodeFocus}
              />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ConfigSidebar; 